import React from 'react';
import {
  // Document icons - clean and modern
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  
  // Media icons - colorful and distinct
  VideoFile as VideoIcon,
  Image as ImageIcon,
  AudioFile as AudioIcon,
  
  // Office suite icons - professional
  Article as WordIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerPointIcon,
  
  // Archive and data icons
  Archive as ArchiveIcon,
  DataObject as JsonIcon,
  Code as CodeIcon,
  
  // Other specific types
  FontDownload as FontIcon,
  Web as WebIcon,
  TextSnippet as TextIcon,
  InsertDriveFile as GenericFileIcon,
} from '@mui/icons-material';
import { SvgIcon } from '@mui/material';

// Custom SVG icons for better visual distinction
const EpubIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
    <text x="8" y="16" fontSize="6" fill="currentColor" fontWeight="bold">E</text>
  </SvgIcon>
);

const CsvIcon = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
    <text x="7" y="16" fontSize="4" fill="currentColor" fontWeight="bold">CSV</text>
  </SvgIcon>
);

export interface FileTypeConfig {
  icon: React.ReactElement;
  label: string;
  color: string;
  bgColor: string;
  category: 'document' | 'media' | 'office' | 'code' | 'archive' | 'web' | 'other';
}

// Modern, comprehensive file type configurations with better color coordination
const fileTypeConfigs: Record<string, FileTypeConfig> = {
  // === Views (authored MDX/pageDoc content) ===
  // Pseudo-MIME (RFC 6838 vendor-prefix). See MIME_TYPE_VIEW in
  // services/content-management-service/src/models/content.model.ts.
  'application/vnd.sharp.view': {
    icon: <WebIcon />,
    label: 'View',
    color: '#7C3AED', // matches theme.palette.fileDots.view
    bgColor: '#EDE9FE', // matches theme.palette.fileTints.view
    category: 'web'
  },

  // === PDF Documents ===
  'application/pdf': {
    icon: <PdfIcon />,
    label: 'PDF Document',
    color: '#e53e3e', // Red - matches common PDF branding
    bgColor: '#fed7d7',
    category: 'document'
  },

  // === Microsoft Office Documents ===
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    icon: <WordIcon />,
    label: 'Word Document',
    color: '#2b6cb0', // Blue - Microsoft Word brand color
    bgColor: '#bee3f8',
    category: 'office'
  },
  'application/msword': {
    icon: <WordIcon />,
    label: 'Word Document (Legacy)',
    color: '#2b6cb0',
    bgColor: '#bee3f8',
    category: 'office'
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    icon: <ExcelIcon />,
    label: 'Excel Spreadsheet',
    color: '#38a169', // Green - Microsoft Excel brand color
    bgColor: '#c6f6d5',
    category: 'office'
  },
  'application/vnd.ms-excel': {
    icon: <ExcelIcon />,
    label: 'Excel Spreadsheet (Legacy)',
    color: '#38a169',
    bgColor: '#c6f6d5',
    category: 'office'
  },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {
    icon: <PowerPointIcon />,
    label: 'PowerPoint Presentation',
    color: '#d69e2e', // Orange - Microsoft PowerPoint brand color
    bgColor: '#fef5e7',
    category: 'office'
  },
  'application/vnd.ms-powerpoint': {
    icon: <PowerPointIcon />,
    label: 'PowerPoint Presentation (Legacy)',
    color: '#d69e2e',
    bgColor: '#fef5e7',
    category: 'office'
  },

  // === Google Workspace ===
  'application/vnd.google-apps.document': {
    icon: <WordIcon />,
    label: 'Google Docs',
    color: '#4285f4', // Google Blue
    bgColor: '#e8f0fe',
    category: 'office'
  },
  'application/vnd.google-apps.spreadsheet': {
    icon: <ExcelIcon />,
    label: 'Google Sheets',
    color: '#34a853', // Google Green
    bgColor: '#e8f5e8',
    category: 'office'
  },
  'application/vnd.google-apps.presentation': {
    icon: <PowerPointIcon />,
    label: 'Google Slides',
    color: '#fbbc04', // Google Yellow
    bgColor: '#fef7cd',
    category: 'office'
  },

  // === Images ===
  'image/jpeg': {
    icon: <ImageIcon />,
    label: 'JPEG Image',
    color: '#805ad5', // Purple
    bgColor: '#e9d8fd',
    category: 'media'
  },
  'image/jpg': {
    icon: <ImageIcon />,
    label: 'JPG Image',
    color: '#805ad5',
    bgColor: '#e9d8fd',
    category: 'media'
  },
  'image/png': {
    icon: <ImageIcon />,
    label: 'PNG Image',
    color: '#805ad5',
    bgColor: '#e9d8fd',
    category: 'media'
  },
  'image/gif': {
    icon: <ImageIcon />,
    label: 'GIF Image',
    color: '#805ad5',
    bgColor: '#e9d8fd',
    category: 'media'
  },
  'image/webp': {
    icon: <ImageIcon />,
    label: 'WebP Image',
    color: '#805ad5',
    bgColor: '#e9d8fd',
    category: 'media'
  },
  'image/svg+xml': {
    icon: <ImageIcon />,
    label: 'SVG Vector Image',
    color: '#805ad5',
    bgColor: '#e9d8fd',
    category: 'media'
  },
  'image/bmp': {
    icon: <ImageIcon />,
    label: 'BMP Image',
    color: '#805ad5',
    bgColor: '#e9d8fd',
    category: 'media'
  },
  'image/tiff': {
    icon: <ImageIcon />,
    label: 'TIFF Image',
    color: '#805ad5',
    bgColor: '#e9d8fd',
    category: 'media'
  },

  // === Videos ===
  'video/mp4': {
    icon: <VideoIcon />,
    label: 'MP4 Video',
    color: '#e53e3e', // Red - common video color
    bgColor: '#fed7d7',
    category: 'media'
  },
  'video/avi': {
    icon: <VideoIcon />,
    label: 'AVI Video',
    color: '#e53e3e',
    bgColor: '#fed7d7',
    category: 'media'
  },
  'video/quicktime': {
    icon: <VideoIcon />,
    label: 'QuickTime Video',
    color: '#e53e3e',
    bgColor: '#fed7d7',
    category: 'media'
  },
  'video/x-msvideo': {
    icon: <VideoIcon />,
    label: 'AVI Video',
    color: '#e53e3e',
    bgColor: '#fed7d7',
    category: 'media'
  },
  'video/webm': {
    icon: <VideoIcon />,
    label: 'WebM Video',
    color: '#e53e3e',
    bgColor: '#fed7d7',
    category: 'media'
  },
  'video/x-ms-wmv': {
    icon: <VideoIcon />,
    label: 'WMV Video',
    color: '#e53e3e',
    bgColor: '#fed7d7',
    category: 'media'
  },
  'video/x-flv': {
    icon: <VideoIcon />,
    label: 'FLV Video',
    color: '#e53e3e',
    bgColor: '#fed7d7',
    category: 'media'
  },

  // === Audio ===
  'audio/mpeg': {
    icon: <AudioIcon />,
    label: 'MP3 Audio',
    color: '#00d4aa', // Teal
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/mp3': {
    icon: <AudioIcon />,
    label: 'MP3 Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/wav': {
    icon: <AudioIcon />,
    label: 'WAV Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/wave': {
    icon: <AudioIcon />,
    label: 'WAV Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/x-wav': {
    icon: <AudioIcon />,
    label: 'WAV Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/ogg': {
    icon: <AudioIcon />,
    label: 'OGG Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/opus': {
    icon: <AudioIcon />,
    label: 'Opus Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/webm': {
    icon: <AudioIcon />,
    label: 'WebM Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/mp4': {
    icon: <AudioIcon />,
    label: 'M4A Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/aac': {
    icon: <AudioIcon />,
    label: 'AAC Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/x-aac': {
    icon: <AudioIcon />,
    label: 'AAC Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/flac': {
    icon: <AudioIcon />,
    label: 'FLAC Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/x-flac': {
    icon: <AudioIcon />,
    label: 'FLAC Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/alac': {
    icon: <AudioIcon />,
    label: 'ALAC Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/x-m4a': {
    icon: <AudioIcon />,
    label: 'M4A Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/wma': {
    icon: <AudioIcon />,
    label: 'WMA Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },
  'audio/x-ms-wma': {
    icon: <AudioIcon />,
    label: 'WMA Audio',
    color: '#00d4aa',
    bgColor: '#b2f5ea',
    category: 'media'
  },

  // === Text and Documents ===
  'text/plain': {
    icon: <TextIcon />,
    label: 'Text File',
    color: '#4a5568', // Gray
    bgColor: '#f7fafc',
    category: 'document'
  },
  'text/rtf': {
    icon: <DocumentIcon />,
    label: 'Rich Text Document',
    color: '#4a5568',
    bgColor: '#f7fafc',
    category: 'document'
  },
  'application/epub+zip': {
    icon: <EpubIcon />,
    label: 'EPUB eBook',
    color: '#6b46c1', // Indigo
    bgColor: '#e0e7ff',
    category: 'document'
  },

  // === Spreadsheets and Data ===
  'text/csv': {
    icon: <CsvIcon />,
    label: 'CSV Data',
    color: '#38a169', // Green
    bgColor: '#c6f6d5',
    category: 'document'
  },
  'application/json': {
    icon: <JsonIcon />,
    label: 'JSON Data',
    color: '#d69e2e', // Orange
    bgColor: '#fef5e7',
    category: 'code'
  },
  'application/xml': {
    icon: <CodeIcon />,
    label: 'XML Data',
    color: '#d69e2e',
    bgColor: '#fef5e7',
    category: 'code'
  },

  // === Web Files ===
  'text/html': {
    icon: <WebIcon />,
    label: 'HTML Document',
    color: '#e53e3e', // Orange-red for HTML
    bgColor: '#fed7d7',
    category: 'web'
  },
  'text/css': {
    icon: <CodeIcon />,
    label: 'CSS Stylesheet',
    color: '#2b6cb0', // Blue for CSS
    bgColor: '#bee3f8',
    category: 'web'
  },
  'application/javascript': {
    icon: <CodeIcon />,
    label: 'JavaScript',
    color: '#d69e2e', // Yellow for JavaScript
    bgColor: '#fef5e7',
    category: 'code'
  },
  'text/javascript': {
    icon: <CodeIcon />,
    label: 'JavaScript',
    color: '#d69e2e',
    bgColor: '#fef5e7',
    category: 'code'
  },

  // === Archives ===
  'application/zip': {
    icon: <ArchiveIcon />,
    label: 'ZIP Archive',
    color: '#4a5568', // Gray
    bgColor: '#f7fafc',
    category: 'archive'
  },
  'application/x-rar-compressed': {
    icon: <ArchiveIcon />,
    label: 'RAR Archive',
    color: '#4a5568',
    bgColor: '#f7fafc',
    category: 'archive'
  },
  'application/x-7z-compressed': {
    icon: <ArchiveIcon />,
    label: '7z Archive',
    color: '#4a5568',
    bgColor: '#f7fafc',
    category: 'archive'
  },
  'application/gzip': {
    icon: <ArchiveIcon />,
    label: 'Gzip Archive',
    color: '#4a5568',
    bgColor: '#f7fafc',
    category: 'archive'
  },

  // === Fonts ===
  'font/ttf': {
    icon: <FontIcon />,
    label: 'TrueType Font',
    color: '#6b46c1', // Indigo
    bgColor: '#e0e7ff',
    category: 'other'
  },
  'font/otf': {
    icon: <FontIcon />,
    label: 'OpenType Font',
    color: '#6b46c1',
    bgColor: '#e0e7ff',
    category: 'other'
  },
  'font/woff': {
    icon: <FontIcon />,
    label: 'WOFF Font',
    color: '#6b46c1',
    bgColor: '#e0e7ff',
    category: 'other'
  },
  'font/woff2': {
    icon: <FontIcon />,
    label: 'WOFF2 Font',
    color: '#6b46c1',
    bgColor: '#e0e7ff',
    category: 'other'
  },
};

// Fallback configurations by category for unknown mime types
const categoryFallbacks: Record<string, FileTypeConfig> = {
  document: {
    icon: <DocumentIcon />,
    label: 'Document',
    color: '#4a5568',
    bgColor: '#f7fafc',
    category: 'document'
  },
  media: {
    icon: <GenericFileIcon />,
    label: 'Media File',
    color: '#805ad5',
    bgColor: '#e9d8fd',
    category: 'media'
  },
  office: {
    icon: <DocumentIcon />,
    label: 'Office Document',
    color: '#2b6cb0',
    bgColor: '#bee3f8',
    category: 'office'
  },
  code: {
    icon: <CodeIcon />,
    label: 'Code File',
    color: '#d69e2e',
    bgColor: '#fef5e7',
    category: 'code'
  },
  archive: {
    icon: <ArchiveIcon />,
    label: 'Archive',
    color: '#4a5568',
    bgColor: '#f7fafc',
    category: 'archive'
  },
  web: {
    icon: <WebIcon />,
    label: 'Web File',
    color: '#e53e3e',
    bgColor: '#fed7d7',
    category: 'web'
  },
  other: {
    icon: <GenericFileIcon />,
    label: 'File',
    color: '#4a5568',
    bgColor: '#f7fafc',
    category: 'other'
  }
};

/**
 * Get file type configuration by mime type with intelligent fallbacks
 */
export const getFileTypeConfig = (mimeType?: string, fileName?: string): FileTypeConfig => {
  if (mimeType && fileTypeConfigs[mimeType.toLowerCase()]) {
    return fileTypeConfigs[mimeType.toLowerCase()];
  }

  // Fallback to extension-based detection if mime type is not recognized
  if (fileName) {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension) {
      const extensionMimeType = getMineMimeTypeFromExtension(extension);
      if (extensionMimeType && fileTypeConfigs[extensionMimeType]) {
        return fileTypeConfigs[extensionMimeType];
      }
    }
  }

  // Final fallback based on mime type category
  if (mimeType) {
    const category = getMimeTypeCategory(mimeType);
    return categoryFallbacks[category] || categoryFallbacks.other;
  }

  return categoryFallbacks.other;
};

/**
 * Get mime type category for intelligent fallbacks
 */
const getMimeTypeCategory = (mimeType: string): string => {
  const type = mimeType.toLowerCase();
  
  if (type.startsWith('image/')) return 'media';
  if (type.startsWith('video/')) return 'media';
  if (type.startsWith('audio/')) return 'media';
  if (type.startsWith('text/')) {
    if (type.includes('html') || type.includes('css') || type.includes('javascript')) return 'web';
    return 'document';
  }
  if (type.includes('word') || type.includes('excel') || type.includes('powerpoint') || 
      type.includes('office') || type.includes('google-apps')) return 'office';
  if (type.includes('zip') || type.includes('rar') || type.includes('7z') || 
      type.includes('gzip') || type.includes('tar')) return 'archive';
  if (type.includes('javascript') || type.includes('json') || type.includes('xml')) return 'code';
  if (type.includes('pdf') || type.includes('document')) return 'document';
  if (type.startsWith('font/')) return 'other';
  
  return 'other';
};

/**
 * Get mime type from file extension as fallback
 */
const getMineMimeTypeFromExtension = (extension: string): string | null => {
  const extensionMap: Record<string, string> = {
    // Documents
    pdf: 'application/pdf',
    txt: 'text/plain',
    rtf: 'text/rtf',
    
    // Microsoft Office
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    
    // Images
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    bmp: 'image/bmp',
    tiff: 'image/tiff',
    
    // Videos
    mp4: 'video/mp4',
    avi: 'video/avi',
    mov: 'video/quicktime',
    webm: 'video/webm',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    
    // Audio
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    wave: 'audio/wave',
    ogg: 'audio/ogg',
    oga: 'audio/ogg',
    opus: 'audio/opus',
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    flac: 'audio/flac',
    alac: 'audio/alac',
    wma: 'audio/x-ms-wma',
    
    // Archives
    zip: 'application/zip',
    rar: 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
    gz: 'application/gzip',
    
    // Web
    html: 'text/html',
    css: 'text/css',
    js: 'application/javascript',
    
    // Data
    json: 'application/json',
    xml: 'application/xml',
    csv: 'text/csv',
    
    // Other
    epub: 'application/epub+zip',
    ttf: 'font/ttf',
    otf: 'font/otf',
    woff: 'font/woff',
    woff2: 'font/woff2',
  };
  
  return extensionMap[extension] || null;
};

/**
 * Get file icon component with consistent styling
 */
export const getFileIcon = (mimeType?: string, fileName?: string, iconProps?: any) => {
  const config = getFileTypeConfig(mimeType, fileName);
  return React.cloneElement(config.icon, {
    style: { color: config.color },
    ...iconProps
  });
};

/**
 * Get file type display name
 */
export const getFileTypeLabel = (mimeType?: string, fileName?: string): string => {
  return getFileTypeConfig(mimeType, fileName).label;
};

/**
 * Get file type color scheme
 */
export const getFileTypeColors = (mimeType?: string, fileName?: string) => {
  const config = getFileTypeConfig(mimeType, fileName);
  return {
    color: config.color,
    bgColor: config.bgColor,
    category: config.category
  };
};

/**
 * Legacy function for backward compatibility with existing getFileIcon usage
 */
export const getFileIconByExtension = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  const mimeType = extension ? getMineMimeTypeFromExtension(extension) : null;
  return getFileIcon(mimeType || undefined, fileName);
};

export default {
  getFileTypeConfig,
  getFileIcon,
  getFileTypeLabel,
  getFileTypeColors,
  getFileIconByExtension
};